import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import WitnessPicker from '../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
} from '../../../helpers/medicineWitness'

const isExpired = (expiry) =>
  moment().startOf('day').isAfter(moment(expiry).startOf('day'))
const isExpiringSoon = (expiry) =>
  !isExpired(expiry) &&
  moment().add(30, 'days').isAfter(moment(expiry).startOf('day'))

const batchKey = (drug) =>
  `${drug.batchNumber ?? ''}|${
    drug.expiry ? moment(drug.expiry).format('YYYY-MM-DD') : 'no-expiry'
  }`
// Undated stock sorts last.
const expiryMs = (expiry) => {
  if (!expiry) return Infinity
  const ms = moment(expiry).valueOf()
  return Number.isNaN(ms) ? Infinity : ms
}

// Why stock is being removed - recorded in the medicine log note.
// The expiry reasons are only offered when they fit the selection.
const OUT_OF_DATE = 'Out of date'
const EXPIRING_SOON = 'Expiring soon'
const OTHER_REASON = 'Other'
const GENERAL_REASONS = [
  'Broken / damaged',
  'Wrong batch number or expiry recorded',
  'Contaminated / seal compromised',
  OTHER_REASON,
]

// Replaces MedicineInitialPopup.jsx and the five nested popups it used to
// open (AssignDrugsPopup, DeleteMedsPopup, MedicineOODPopup,
// MedicineAlmostOODPopup, DiscardReusable) - one drawer, one batch table
// with quick-select shortcuts, instead of one popup-per-action handling a
// single batch at a time. Same underlying endpoints, same policy (what's
// allowed where), just consolidated.
const MedicineDrawer = ({
  selectedMedicines,
  setSelectedMedicines,
  location,
  loadPageAPI,
}) => {
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  // Governance can remove in-date stock straight from a bag (broken vial,
  // wrong batch recorded...); everyone else returns it to a store.
  const canRemoveInDate =
    !!user?.employee?.teams?.includes('Governance') ||
    !!user?.security?.isSiteAdmin
  const [selectedCounts, setSelectedCounts] = useState({})
  const [moveMode, setMoveMode] = useState(null) // 'bag' | 'store' | null
  const [locationOptions, setLocationOptions] = useState([])
  const [moveTarget, setMoveTarget] = useState('')
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removeReason, setRemoveReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  // Controlled drugs: destruction - and discarding an empty reusable
  // container - must be witnessed by a colleague or an external person
  // (name + contact details). The remove panel carries its own witness
  // picker; this one is only for discarding: 'discard' | null (hidden).
  const [witnessFor, setWitnessFor] = useState(null)
  const [witness, setWitness] = useState(emptyWitness())
  const [submitting, setSubmitting] = useState(false)

  const open = selectedMedicines.length > 0
  const isBag = location?.type !== 'Medicine Store'
  const isReusable = !!selectedMedicines[0]?.medicine?.reuse
  const isControlled = !!selectedMedicines[0]?.medicine?.isControlled
  const medName = selectedMedicines[0]?.medicine?.title

  // A stock "batch" here is batch number AND expiry. The same batch number
  // can (rarely) be held with two different expiry dates; one row per
  // number would hide the in-date units behind the expired ones, so
  // "remove" swept up both and "move" could not pick out the good ones.
  // Mirrors batchKey() in server/src/helpers/medsMx/affectedMeds.js.
  const batches = useMemo(() => {
    const groups = selectedMedicines.reduce((acc, drug) => {
      const key = batchKey(drug)
      if (!acc[key]) {
        acc[key] = {
          key,
          batchNumber: drug.batchNumber,
          expiry: drug.expiry,
          rows: [],
        }
      }
      acc[key].rows.push(drug)
      return acc
    }, {})
    // Soonest expiry first, so anything needing attention sits at the top
    // and same-number batches with different expiries land next to each
    // other in date order.
    return Object.values(groups).sort((a, b) => {
      const aMs = expiryMs(a.expiry)
      const bMs = expiryMs(b.expiry)
      if (aMs !== bMs) return aMs < bMs ? -1 : 1
      return String(a.batchNumber ?? '').localeCompare(
        String(b.batchNumber ?? ''),
      )
    })
  }, [selectedMedicines])

  const batchByKey = useMemo(
    () => Object.fromEntries(batches.map((batch) => [batch.key, batch])),
    [batches],
  )

  // How many rows (expiry dates) each batch number spans - flagged in the
  // table so two rows for "the same" batch don't look like a glitch. Kept
  // to a short count: a long caption widens the Batch column and squeezes
  // the quantity stepper into wrapping.
  const expiryCountByBatchNumber = useMemo(() => {
    const counts = new Map()
    batches.forEach((batch) =>
      counts.set(batch.batchNumber, (counts.get(batch.batchNumber) || 0) + 1),
    )
    return counts
  }, [batches])

  const batchStatus = (batch) => {
    if (!batch) return 'good'
    if (isExpired(batch.expiry)) return 'expired'
    if (isExpiringSoon(batch.expiry)) return 'expiringSoon'
    return 'good'
  }

  const resetSelection = () => setSelectedCounts({})

  useEffect(() => {
    resetSelection()
    setMoveMode(null)
    setRemoveOpen(false)
    setRemoveReason('')
    setOtherReason('')
    setWitnessFor(null)
    setWitness(emptyWitness())
  }, [selectedMedicines])

  const selectAll = (predicate) => {
    const next = {}
    batches.forEach((batch) => {
      next[batch.key] = predicate(batch) ? batch.rows.length : 0
    })
    setSelectedCounts(next)
  }

  const setBatchCount = (key, count) => {
    const max = batchByKey[key]?.rows.length ?? 0
    setSelectedCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, Math.min(max, count)),
    }))
  }

  const selectedRecords = useMemo(() => {
    return Object.entries(selectedCounts).flatMap(([key, count]) =>
      (batchByKey[key]?.rows || []).slice(0, count),
    )
  }, [selectedCounts, batchByKey])

  const selectedIds = selectedRecords.map((r) => r._id)
  const selectedTotal = selectedIds.length

  const selectionIsAllExpiredOrSoon =
    selectedTotal > 0 &&
    selectedRecords.every(
      (r) => isExpired(r.expiry) || isExpiringSoon(r.expiry),
    )
  const canRemoveSelection =
    selectedTotal > 0 && (selectionIsAllExpiredOrSoon || canRemoveInDate)
  const selectionHasInDate = selectedRecords.some((r) => !isExpired(r.expiry))
  const selectionHasExpired = selectedRecords.some((r) => isExpired(r.expiry))
  const selectionHasExpiringSoon = selectedRecords.some((r) =>
    isExpiringSoon(r.expiry),
  )
  const removeReasons = [
    ...(selectionHasExpired ? [OUT_OF_DATE] : []),
    ...(selectionHasExpiringSoon ? [EXPIRING_SOON] : []),
    ...GENERAL_REASONS,
  ]

  // The batch table stays editable while the panel is open - drop a reason
  // the new selection no longer supports.
  useEffect(() => {
    if (removeReason && !removeReasons.includes(removeReason)) {
      setRemoveReason('')
    }
  }, [removeReason, selectionHasExpired, selectionHasExpiringSoon])

  const defaultRemoveReason = () => {
    if (!selectionIsAllExpiredOrSoon) return ''
    return selectionHasExpired ? OUT_OF_DATE : EXPIRING_SOON
  }

  const openRemove = () => {
    setRemoveReason(defaultRemoveReason())
    setOtherReason('')
    setWitness(emptyWitness())
    setRemoveOpen(true)
  }

  const close = () => setSelectedMedicines([])

  const afterAction = (message) => {
    setToast?.({ message })
    loadPageAPI()
    close()
  }

  useEffect(() => {
    setMoveTarget('')
    if (!moveMode) return
    API.get(moveMode === 'store' ? '/medsmx/stores' : '/medsmx/bags')
      .then((res) =>
        setLocationOptions(res.data.filter((l) => l._id !== location?._id)),
      )
      .catch((e) => console.log(e))
  }, [moveMode])

  // Expired items only travel to stores that accept out-of-date medicines
  // (quarantine stores) - never into a bag or a regular store.
  const targetLocation = locationOptions.find((l) => l._id === moveTarget)
  const targetAllowsExpired =
    moveMode === 'store' && !!targetLocation?.allowExpiredMedicines
  const idsToMove = targetAllowsExpired
    ? selectedIds
    : selectedRecords.filter((r) => !isExpired(r.expiry)).map((r) => r._id)

  const handleMove = async () => {
    if (!moveTarget || idsToMove.length === 0) return
    setSubmitting(true)
    try {
      const res = await API.post('/medsmx/assign-to-bag', {
        medsToMove: idsToMove,
        newLocation: moveTarget,
        medicineDescription: selectedMedicines[0].medicine._id,
        movedFrom: location._id,
      })
      if (res.status === 200) {
        afterAction(`${idsToMove.length} ${medName} moved`)
      } else {
        setToast?.({ message: 'Failed to move medicine', severity: 'error' })
      }
    } catch (e) {
      console.error(e)
      setToast?.({ message: 'Failed to move medicine', severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const witnessValid = isWitnessComplete(witness)

  // Witness payload for the server - only controlled drugs carry one.
  const buildWitness = () =>
    isControlled ? toWitnessPayload(witness) : undefined

  const resolvedReason =
    removeReason === OTHER_REASON ? otherReason.trim() : removeReason
  const removeValid = !!resolvedReason && (!isControlled || witnessValid)

  const handleRemoveSelected = async () => {
    setSubmitting(true)
    try {
      const res = await API.post('/medicines-management/delete-ood', {
        medsToDelete: selectedIds,
        medicineDescription: selectedMedicines[0].medicine,
        deletedFrom: location._id,
        reason: resolvedReason,
        witness: buildWitness(),
      })
      if (res.status === 200) {
        afterAction(`${selectedTotal} ${medName} removed`)
      } else {
        setToast?.({
          message:
            typeof res.data === 'string' && res.data
              ? res.data
              : 'Failed to remove medicine',
          severity: 'error',
        })
      }
    } catch (e) {
      console.error(e)
      setToast?.({ message: 'Failed to remove medicine', severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDiscardReusable = async () => {
    setSubmitting(true)
    // One request per container, so a failure part-way leaves the earlier
    // ones discarded - refresh in that case so the page reflects it.
    let discarded = 0
    try {
      const witness = buildWitness()
      for (const record of selectedRecords) {
        // eslint-disable-next-line no-await-in-loop
        const res = await API.post('/medsmx/dispose-empty-bottle', {
          id: record._id,
          locationID: location._id,
          batchNumber: record.batchNumber,
          title: record.medicine.title,
          expiry: record.expiry,
          witness,
        })
        // API never rejects on 4xx/5xx - check the status ourselves.
        if (res.status !== 200) {
          throw new Error(
            typeof res.data === 'string' && res.data
              ? res.data
              : 'Failed to discard container(s)',
          )
        }
        discarded += 1
      }
      afterAction(`${selectedTotal} empty container(s) discarded`)
    } catch (e) {
      console.error(e)
      setToast?.({
        message: e?.message || 'Failed to discard container(s)',
        severity: 'error',
      })
      if (discarded > 0) loadPageAPI()
    } finally {
      setSubmitting(false)
    }
  }

  const statusColor = {
    expired: 'error.main',
    expiringSoon: 'warning.main',
    good: 'text.primary',
  }
  const statusLabel = {
    expired: 'Expired',
    expiringSoon: 'Expiring soon',
    good: '',
  }

  return (
    <Drawer anchor="right" open={open} onClose={close}>
      <Stack sx={{ width: { xs: '100vw', sm: 480 }, p: 2 }} gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack>
            <Typography variant="title2" fontSize="1.2rem">
              {medName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedMedicines[0]?.medicine?.constitution}{' '}
              {selectedMedicines[0]?.medicine?.presentation}
            </Typography>
            {isControlled && (
              <Chip
                size="small"
                color="error"
                label="Controlled Drug"
                sx={{ mt: 0.5, width: 'fit-content' }}
              />
            )}
          </Stack>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        {selectedTotal === 0 && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Choose how many to act on — tick a batch to take all of it, or type
            a quantity — then pick an action below, e.g.{' '}
            <strong>{isBag ? 'Move to another bag' : 'Assign to a bag'}</strong>
            .
          </Alert>
        )}

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectAll((b) => batchStatus(b) === 'expired')}
          >
            Select all expired
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectAll((b) => batchStatus(b) === 'expiringSoon')}
          >
            Select all expiring soon
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectAll(() => true)}
          >
            Select all
          </Button>
          <Button size="small" onClick={resetSelection}>
            Clear
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Batch</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Qty selected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((batch) => {
              const { key, batchNumber, expiry, rows } = batch
              const status = batchStatus(batch)
              const count = selectedCounts[key] || 0
              const expiryLabel = moment(expiry).format('DD/MM/YYYY')
              return (
                <TableRow key={key}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={count > 0}
                      indeterminate={count > 0 && count < rows.length}
                      onChange={(e) =>
                        setBatchCount(key, e.target.checked ? rows.length : 0)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">{batchNumber}</Typography>
                      {expiryCountByBatchNumber.get(batchNumber) > 1 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {expiryCountByBatchNumber.get(batchNumber)} expiry
                          dates
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography
                        color={statusColor[status]}
                        fontWeight={status !== 'good' ? 700 : 400}
                      >
                        {expiryLabel}
                      </Typography>
                      {statusLabel[status] && (
                        <Typography
                          variant="caption"
                          color={statusColor[status]}
                        >
                          {statusLabel[status]}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {rows.length > 1 ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.25}
                      >
                        <IconButton
                          size="small"
                          aria-label="One fewer"
                          onClick={() => setBatchCount(key, count - 1)}
                          disabled={count <= 0}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={count}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10)
                            setBatchCount(
                              key,
                              Number.isNaN(parsed) ? 0 : parsed,
                            )
                          }}
                          onFocus={(e) => e.target.select()}
                          size="small"
                          inputProps={{
                            inputMode: 'numeric',
                            'aria-label': `Quantity from batch ${batchNumber} expiring ${expiryLabel}`,
                            style: {
                              textAlign: 'center',
                              width: 34,
                              padding: '4px 2px',
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          / {rows.length}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="One more"
                          onClick={() => setBatchCount(key, count + 1)}
                          disabled={count >= rows.length}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : (
                      `${count} / ${rows.length}`
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <Divider />

        <Stack gap={1.5}>
          <Typography variant="subtitle2" color="text.secondary">
            {selectedTotal > 0
              ? `${selectedTotal} selected`
              : 'Select batches above to act on them'}
          </Typography>

          {moveMode && (
            <Stack gap={1} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              {selectedRecords.some((r) => isExpired(r.expiry)) &&
                (targetAllowsExpired ? (
                  <Alert severity="info" variant="outlined">
                    This store accepts out of date medicines, so the expired
                    items in your selection will be moved too.
                  </Alert>
                ) : (
                  <Alert severity="warning" variant="outlined">
                    Out of date items in your selection won&apos;t be included
                    in the move.
                  </Alert>
                ))}
              {!!moveTarget && idsToMove.length === 0 && (
                <Alert severity="error" variant="outlined">
                  Everything selected is out of date, so nothing would be moved
                  to this location. Use Remove selected instead, or move them to
                  a store that accepts expired medicines.
                </Alert>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>
                  {moveMode === 'store'
                    ? isBag
                      ? 'Return to store'
                      : 'Move to store'
                    : 'Move into'}
                </InputLabel>
                <Select
                  label={
                    moveMode === 'store'
                      ? isBag
                        ? 'Return to store'
                        : 'Move to store'
                      : 'Move into'
                  }
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                >
                  {locationOptions.map((loc) => (
                    <MenuItem key={loc._id} value={loc._id}>
                      {loc.name}{' '}
                      {loc.level && loc.level !== 'false'
                        ? `(${loc.level})`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" gap={1}>
                <Button onClick={() => setMoveMode(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={!moveTarget || submitting || idsToMove.length === 0}
                  onClick={handleMove}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          )}

          {witnessFor === 'discard' && (
            <Stack gap={1.5} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Alert severity="warning" variant="outlined">
                {`Discarding an empty controlled drug container must be witnessed. Record who witnessed the disposal of ${selectedTotal} × ${medName} container(s).`}
              </Alert>
              <WitnessPicker value={witness} onChange={setWitness} />
              <Stack direction="row" gap={1}>
                <Button onClick={() => setWitnessFor(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!witnessValid || submitting}
                  onClick={handleDiscardReusable}
                >
                  Confirm Discard
                </Button>
              </Stack>
            </Stack>
          )}

          {removeOpen && (
            <Stack gap={1.5} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              {selectionHasInDate && (
                <Alert severity="info" variant="outlined">
                  In-date stock will be marked as disposed and logged against
                  this location.
                </Alert>
              )}
              <FormControl fullWidth size="small" required>
                <InputLabel>Reason for removal</InputLabel>
                <Select
                  label="Reason for removal"
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                >
                  {removeReasons.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {removeReason === OTHER_REASON && (
                <TextField
                  size="small"
                  required
                  label="Describe the reason"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
              )}
              {isControlled && (
                <>
                  <Alert severity="warning" variant="outlined">
                    {`Destroying a controlled drug must be witnessed. Record who witnessed the destruction of ${selectedTotal} × ${medName}.`}
                  </Alert>
                  <WitnessPicker value={witness} onChange={setWitness} />
                </>
              )}
              <Stack direction="row" gap={1}>
                <Button onClick={() => setRemoveOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!removeValid || submitting}
                  onClick={handleRemoveSelected}
                >
                  {isControlled ? 'Confirm Destruction' : 'Confirm Removal'}
                </Button>
              </Stack>
            </Stack>
          )}

          {!moveMode && !removeOpen && !witnessFor && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Button
                size="small"
                variant={isBag ? 'outlined' : 'contained'}
                disabled={selectedTotal === 0}
                onClick={() => setMoveMode('bag')}
              >
                {isBag ? 'Move to another bag' : 'Assign to a bag'}
              </Button>
              {isBag ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  disabled={selectedTotal === 0}
                  onClick={() => setMoveMode('store')}
                >
                  Return to store
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={selectedTotal === 0}
                  onClick={() => setMoveMode('store')}
                >
                  Move to another store
                </Button>
              )}
              {isReusable && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={selectedTotal === 0 || submitting}
                  onClick={() =>
                    isControlled
                      ? setWitnessFor('discard')
                      : handleDiscardReusable()
                  }
                >
                  Discard empty container(s)
                </Button>
              )}
              <Tooltip
                title={
                  selectedTotal > 0 && !canRemoveSelection
                    ? 'Only Governance can remove in-date medicines - deselect them, or return them to a store instead.'
                    : ''
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={!canRemoveSelection || submitting}
                    onClick={openRemove}
                  >
                    Remove selected
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default MedicineDrawer
